// THE PEOPLE BOARD ('#people') — the whole register at once, ranked by the rule that fired.
//
// The operator arrives with one question: "of everyone I know, who needs a move from me
// today?" — and must leave either inside one person's brief WITH A REASON, or certain that
// nothing is waiting. The alphabet cannot answer that question, so the sort key is the rule:
//
//   0  the ball is with us and it is late
//   1  the ball is with us
//   2  cooling — days since the last interaction exceed this person's tier cadence
//   3  everyone else
//   …  and NAME only as the last tie-break
//
// Those first three are the whole of "needs a move". Being new on the record is a fact about
// a person, not a claim on the operator's time — a freshly imported person with no open ball
// and no cadence blown yet is NOT a move, so "new" never appears in this ladder. It gets its
// own chip instead ("New · 7d", counted from the rail's `new_7d`), next to Tier A / Cooling /
// Candidates / Everyone.
//
// The rank, the state and every row's second line are computed ONCE, server-side
// (src/server/routes/persons.ts). This page ARRANGES what that rule emitted; it never
// re-derives it, so the board and a person's brief can never disagree about who is late.
//
// THE SEARCH IS EXACT, NOT PARTIAL. `/persons/board` returns every non-archived person, so
// filtering here narrows a complete set rather than showing the first page of a bigger one.
// If that ever stops being true, this filter has to move to the server the same day.
//
// THE PAGE IS BOUNDED. The board holds the whole register — 279 people after the seed import —
// so rendering the filtered set whole produced one endless scroll with the lead card stranded
// at the top of it. The zone therefore draws PAGE_SIZE rows and offers 'Show 50 more' at the
// foot; the count in the zone head and the HELD figure in the rail stay the size of the real
// set, so the page never pretends the register is smaller than it is. Filtering or searching
// starts again at one page.
//
// ROUND 7 ADDS THE ONE THING THIS PAGE DOES NOT ALREADY HOLD (BUILD-BRIEF-R7 §6.2). The board
// is the whole register, so the chips, the sort and the search narrow what is already here. A
// PERSON RASTER CELL is not on the board at all — it lives in `facts` and in `persons_computed`
// — so the ten cell selects go to `GET /api/persons`, which answers with the ids, the values
// themselves (for the row chips) and the three figures of the count line. The page narrows to
// the ids it is handed and derives nothing: which fact answers a cell, and whether anybody held
// it, is the server's rule, exactly as the tree filter's membership is.
//
// The count line is never a bare number. «n held · m estimated · k from the title» always
// travels with the CELLS it was counted on and with the SIZE of the set it was counted over,
// because three zeros mean "nobody's function is on the record" and must never be read as
// "nobody matched".
//
// The dress is BUILD-BRIEF §2: mast → five-cell rail → chips band → zones on sheets. No stat
// cards, no category hues; the row's tag carries the tone and the figure carries the count.
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import CandidatesZone from '../components/CandidatesZone.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState from '../components/EmptyState.js';
import {
  Att, Fig, FilterSelect, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Zone, ZoneHead,
  type FilterOption, type RailTone, type TagTone,
} from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { usePoll } from '../components/usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { open as openRoute, ROUTES, useDeepLink, useRouteParams } from '../routes.js';
import {
  createPerson, getBoard, listPersons,
  type PeopleBoard, type PersonCard, type PersonListCell, type PersonListCounts,
  type PersonListFilters,
} from '../api/persons.js';
import { getOrganisation } from '../api/organisations.js';
import { listCandidates } from '../api/personas.js';
import { listScores } from '../api/scoring.js';
// THE VOCABULARY IS THE RASTER'S OWN (BUILD-BRIEF-R7 §6.2). Every option, every chip and every
// filter label below is read out of `person-raster.v1.ts` through `personLabelFor` — this page
// never spells a cell name or a token a second time, in any language.
import {
  SENIORITIES, findPersonCell, personCellKey, personLabelFor,
} from '../../shared/person-raster.v1.js';
import type { RasterLang } from '../../shared/raster.v1.js';
import type { FactMethod, Tier } from '../../shared/types.js';
import './PeoplePage.de.js';
import './PeoplePage.fr.js';

/** A triage surface, not a live console. */
const POLL_MS = 30_000;

/** Rows drawn before the foot offers more. One screenful of scrolling, not twelve. */
const PAGE_SIZE = 50;

/** What `GET /api/persons` will answer in one page — the SERVER's own maximum, not a budget
 *  invented here. The cell segment is read in pages of it until the server's own `total` is met. */
const SERVER_PAGE = 500;

type Chip = 'needs_move' | 'tier_a' | 'cooling' | 'new' | 'candidates' | 'everyone';

/**
 * How the SAME filtered set is ordered. `rule` is the board's own ladder, composed server-side
 * (the file header's 0–3). `score` re-reads it by the number `services/scoring.ts` produced —
 * the same reasons, weighted. A person nobody has scored sorts LAST under `score`, never as a
 * zero: no score is not a low score.
 */
type Sort = 'rule' | 'score';

const SORT_LABEL: Record<Sort, string> = { rule: 'By rule', score: 'By score' };

const CHIP_LABEL: Record<Chip, string> = {
  needs_move: 'Needs a move',
  tier_a: 'Tier A',
  cooling: 'Cooling',
  new: 'New · 7d',
  candidates: 'Candidates',
  everyone: 'Everyone',
};

/**
 * The row's tag. A tone is a CONDITION, never a category: only `late` and `cooling` want the
 * operator, so only they are coloured. `warm` is the ordinary case and takes the plain tag —
 * colouring the normal state is how a board stops meaning anything.
 */
const STATE_TONE: Record<PersonCard['state'], TagTone> = {
  late: 'broken',
  cooling: 'attend',
  new: 'neutral',
  warm: 'neutral',
  quiet: 'neutral',
};

const STATE_LABEL: Record<PersonCard['state'], string> = {
  late: 'late',
  cooling: 'cooling',
  new: 'new',
  warm: 'warm',
  quiet: 'quiet',
};

/** `1fr 190px 80px 80px 50px 60px` — name+note · ball · last contact · tier · score · tag.
 *  The mockup's five, plus the score slot round 2 adds; the score cell is empty (not a zero)
 *  for anyone the rules have not scored. */
const ROW_COLUMNS = { gridTemplateColumns: '1fr 190px 80px 80px 50px 60px' } as const;

// ═══ the cell filters (BUILD-BRIEF-R7 §6.2) ═══════════════════════════════════
//
// Ten questions the operator may put to the register, each one a RASTER CELL. The page owns no
// vocabulary of its own: the parameter names are the ones `routes/persons.ts` answers
// (`PERSON_CELL_FILTERS` there, `seniority_min` beside it), and every label and every option is
// read out of the module through `personLabelFor` — the cell's own word, in the console's
// language, and never a second spelling.

type TFn = (key: string, vars?: Record<string, string | number>) => string;

export interface CellFilterSpec {
  /** The query parameter GET /api/persons answers. */
  param: string;
  /** `person.<block>.<cell>` — where the label and the options come from. */
  key: string;
  /** A MINIMUM RANK, not a token to match (§1.2): every option reads «… and above». */
  ordered?: true;
}

/** The ten of §6.2, in the order the band draws them: the job first, then reach, then history. */
export const CELL_FILTERS: readonly CellFilterSpec[] = [
  { param: 'function_family', key: personCellKey('job', 'function_family') },
  { param: 'seniority_min', key: personCellKey('job', 'seniority'), ordered: true },
  { param: 'buying_influence', key: personCellKey('job', 'buying_influence') },
  { param: 'register_function', key: personCellKey('governance', 'register_function') },
  { param: 'work_canton', key: personCellKey('contact', 'work_canton') },
  { param: 'email_status', key: personCellKey('contact', 'email_status') },
  { param: 'phone_kind', key: personCellKey('contact', 'phone_kind') },
  { param: 'years_in_role', key: personCellKey('job', 'years_in_role') },
  { param: 'birth_decade', key: personCellKey('person', 'birth_decade') },
  { param: 'recency_band', key: personCellKey('engagement', 'recency_band') },
];

/** The cell's own name, in the console's language. Exported so the test reads the same string the page draws. */
export function cellFilterLabel(spec: CellFilterSpec, lang: RasterLang): string {
  const found = findPersonCell(spec.key);
  return found ? personLabelFor(lang, found.block.key, found.cell.key) : spec.key;
}

/**
 * The select's options — the CELL'S OWN vocabulary, in raster order, labelled by
 * `personLabelFor`. An ordered filter (`seniority_min`) lists the ranks from the top down and
 * says what a minimum means in words, because «Cadre supérieur» alone would read as an equality
 * the server does not perform.
 */
export function cellFilterOptions(spec: CellFilterSpec, lang: RasterLang, t: TFn): FilterOption[] {
  const found = findPersonCell(spec.key);
  if (!found) return [];
  const { block, cell } = found;
  const tokens = spec.ordered
    ? [...SENIORITIES]
    : [...(cell.values ?? []), ...(cell.bands ?? [])].map((v) => v.value);
  return tokens.map((value) => {
    const label = personLabelFor(lang, block.key, cell.key, value);
    return { value, label: spec.ordered ? t('{rank} and above', { rank: label }) : label };
  });
}

/** The chosen token, in the words the select itself shows — never the raw token. */
export function cellFilterValueLabel(spec: CellFilterSpec, value: string, lang: RasterLang, t: TFn): string {
  return cellFilterOptions(spec, lang, t).find((o) => o.value === value)?.label ?? value;
}

/** The cells the three figures describe, as a reader's list: «Function family · Seniority · Buying influence». */
export function countedCellLabels(cells: readonly string[], lang: RasterLang): string {
  return cells
    .map((key) => {
      const found = findPersonCell(key);
      return found ? personLabelFor(lang, found.block.key, found.cell.key) : key;
    })
    .join(' · ');
}

/**
 * THE HONEST COUNT (§6.2) — «n held · m estimated · k from the title», and under it, in the
 * `.fd` voice, WHICH CELLS the three are about and how many people they were counted over.
 *
 * A segment built on last night's table is never shown as a bare number, and three zeros here
 * must never read as "nobody matched": they mean nobody's function is on the record, which is
 * why the sentence carries the matched total beside the cell names. The three figures are
 * arithmetic, so they carry NO claim mark (P12).
 */
export function CellCountLine({ counts, total, lang }: {
  counts: PersonListCounts;
  /** How many people the cell filters matched — the set the three figures were counted over. */
  total: number;
  lang: RasterLang;
}) {
  const t = useT();
  const cells = countedCellLabels(counts.cells, lang);
  const about = t('counted on {cells} · over {n} people the cell filters match', { cells, n: total });
  return (
    <>
      <div className="mast-sub">
        <span><Fig>{counts.held}</Fig> {t('held')}</span>
        <Sep />
        <span><Fig>{counts.estimated}</Fig> {t('estimated')}</span>
        <Sep />
        <span><Fig>{counts.from_title}</Fig> {t('from the title')}</span>
      </div>
      <span className="fd" title={about}>{about}</span>
    </>
  );
}

/** How a cell value came to be, said in words in the chip's title. The five never look the same (P12). */
const METHOD_SENTENCE: Record<FactMethod, string> = {
  read: '{cell}: {value} · read from a source',
  estimated: '{cell}: {value} · estimated by an agent',
  computed: '{cell}: {value} · computed by the nightly job',
  declared: '{cell}: {value} · declared by you',
  not_found: '{cell}: {value} · searched for and not found',
};

/**
 * The value of ONE filtered cell, on one row, with its mark: ◇ for a claim nobody has held, ▪
 * for one an operator committed to, and NOTHING AT ALL for a computed value — arithmetic makes
 * no claim. An estimate additionally wears the ghost dash, the shape a soft value takes
 * everywhere else in this console.
 */
export function CellChip({ cellKey, cell, lang }: { cellKey: string; cell: PersonListCell; lang: RasterLang }) {
  const t = useT();
  const found = findPersonCell(cellKey);
  if (!found) return null;
  const name = personLabelFor(lang, found.block.key, found.cell.key);
  const label = personLabelFor(lang, found.block.key, found.cell.key, cell.value);
  const computed = cell.method === 'computed';
  return (
    <Tag
      tone={cell.method === 'estimated' ? 'ghost' : 'neutral'}
      title={t(METHOD_SENTENCE[cell.method] ?? METHOD_SENTENCE.read, { cell: name, value: label })}
    >
      {!computed && <><Att kind={cell.held ? 'held' : 'proposed'} />{' '}</>}
      {label}
    </Tag>
  );
}

/** Every filtered cell this person carries, in the band's own order. Nothing when none was asked. */
export function CellChips({ cells, lang }: { cells: Record<string, PersonListCell> | undefined; lang: RasterLang }) {
  if (!cells) return null;
  const present = CELL_FILTERS.filter((f) => cells[f.key] !== undefined);
  if (present.length === 0) return null;
  return (
    <>
      {present.map((f) => (
        <Fragment key={f.param}>
          {' '}<CellChip cellKey={f.key} cell={cells[f.key]} lang={lang} />
        </Fragment>
      ))}
    </>
  );
}

export default function PeoplePage() {
  const t = useT();
  const { fmtDate, lang: uiLang } = useLocale();
  const lang = uiLang as RasterLang;
  const toast = useToast();
  const params = useRouteParams();

  const load = useCallback(() => getBoard(), []);
  const { data: board, loading, error, notice, refresh } = usePoll<PeopleBoard>(load, POLL_MS);

  /**
   * The scores, read once beside the board. They are a SEPARATE producer: a board that answers
   * while scoring has never run is a board with no scores, and the row simply carries no figure
   * — not a zero. A failed read leaves the map empty for the same reason.
   */
  const [scores, setScores] = useState<Map<number, number>>(new Map());
  useEffect(() => {
    let live = true;
    listScores()
      .then((r) => { if (live) setScores(new Map(r.items.map((s) => [s.person_id, s.score]))); })
      .catch(() => { if (live) setScores(new Map()); });
    return () => { live = false; };
  }, [board?.total]);

  /**
   * The candidates, read from the SAME endpoint the Candidates zone reads.
   *
   * `board.rail.candidates` counts only `persons` rows sitting at status 'candidate'; the zone
   * (`GET /personas/candidates`) also lists unheld `candidate` PROPOSALS that have not minted a
   * person yet — which is what a lookalike run files. Reading the board here put "CANDIDATES 0 ·
   * nothing proposed itself as a person" directly above a zone headed "CANDIDATES 1", so the
   * cell now reads the zone's own producer and the two can no longer disagree.
   *
   * `null` is not zero: nobody has answered yet, or the read failed, and the cell says so
   * (`unknown` → "not collected") rather than inventing a counted zero.
   */
  const [candidates, setCandidates] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    listCandidates()
      .then((r) => { if (live) setCandidates(r.total); })
      .catch(() => { if (live) setCandidates(null); });
    return () => { live = false; };
  }, [board?.total]);

  /**
   * THE CELL FILTERS (§6.2) — the ONE thing on this page the SERVER answers.
   *
   * The board is the whole register, so the chips and the search narrow what is already here;
   * a raster cell is not on the board at all, so the ten selects go to `GET /api/persons`,
   * which reads a held/estimated/declared fact first and last night's table only where nobody
   * has spoken. What comes back is a set of ids (the rows are narrowed to it, exactly as the
   * tree filter narrows them), the values themselves for the chips, and the three figures.
   *
   * The read runs even with no filter selected, because the count line still has to say what
   * the register knows about the three job cells. A failed read leaves NO count line and says
   * so — three blank figures would be read as three zeros.
   */
  const [cellSel, setCellSel] = useState<Record<string, string>>({});
  const [segment, setSegment] = useState<{
    ids: Set<number> | null;
    cells: Map<number, Record<string, PersonListCell>>;
    counts: PersonListCounts;
    total: number;
  } | null>(null);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const activeCells = useMemo(
    () => CELL_FILTERS.filter((f) => (cellSel[f.param] ?? '') !== ''),
    [cellSel],
  );

  useEffect(() => {
    let live = true;
    const active = CELL_FILTERS.filter((f) => (cellSel[f.param] ?? '') !== '');
    const base: PersonListFilters = {};
    for (const f of active) base[f.param] = cellSel[f.param];
    void (async () => {
      try {
        // With no cell filter there is nothing to narrow and nothing to chip: one row is enough
        // to carry the counts, which the server computes over the whole filtered set anyway.
        const first = await listPersons({ ...base, limit: active.length ? SERVER_PAGE : 1 });
        const items = active.length ? [...first.items] : [];
        // A segment larger than one page IS READ TO THE END. The count line says how many people
        // the cells matched; narrowing the board to the first page while claiming that number
        // would put a different set on screen from the one the sentence describes. The loop's
        // bound is the server's own answer — its `total` and an empty page — not a number
        // invented here.
        while (active.length && items.length < first.total) {
          const next = await listPersons({ ...base, limit: SERVER_PAGE, offset: items.length });
          if (next.items.length === 0) break;
          items.push(...next.items);
        }
        if (!live) return;
        setSegmentError(null);
        setSegment({
          ids: active.length ? new Set(items.map((p) => p.id)) : null,
          cells: new Map(items.map((p) => [p.id, p.cells ?? {}])),
          counts: first.counts,
          total: first.total,
        });
      } catch (err) {
        if (!live) return;
        setSegment(null);
        setSegmentError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { live = false; };
  }, [cellSel, board?.total]);

  const [chip, setChip] = useState<Chip>('needs_move');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [sort, setSort] = useState<Sort>('rule');
  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState<{ name: string; tier: Tier; role: string }>({ name: '', tier: 'C', role: '' });

  /**
   * THE TREE FILTER (BUILD-BRIEF-R3b §2b) — "all people in this tree", arriving from an
   * organisation's Units zone as `#people?tree_of=N`.
   *
   * The MEMBERSHIP is the server's answer (`GET /persons?tree_of=N` walks `parent_id`
   * recursively): this page narrows the board it already holds to those ids, and never tries to
   * work out a tree from a parent id of its own. A read that fails clears the filter and says
   * so — a chip claiming a tree while showing everybody would be a lie about what is on screen.
   */
  const [treeOf, setTreeOf] = useState<{ id: number; ids: Set<number>; label: string } | null>(null);
  const [treeWanted, setTreeWanted] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    if (treeWanted === null) { setTreeOf(null); return; }
    const id = treeWanted;
    void (async () => {
      try {
        const [people, org] = await Promise.all([
          listPersons({ tree_of: id, limit: 1000 }),
          getOrganisation(id).catch(() => null),
        ]);
        if (!live) return;
        setTreeOf({
          id,
          ids: new Set(people.items.map((p) => p.id)),
          label: org ? org.organisation.name : `#${id}`,
        });
      } catch (err) {
        if (!live) return;
        setTreeWanted(null);
        setTreeOf(null);
        toast('err', err instanceof Error
          ? t('The tree could not be read: {error}', { error: err.message })
          : t('The tree could not be read.'));
      }
    })();
    return () => { live = false; };
  }, [treeWanted, toast, t]);

  // Arrival intents (routes.ts law 2): `#people?tier=A`, `#people?q=…`, `#people?status=candidate`,
  // `#people?tree_of=N`.
  useDeepLink(params, {
    q: (v) => { setQuery(v); setChip('everyone'); },
    tier: (v) => { if (v === 'A') setChip('tier_a'); },
    status: (v) => { if (v === 'candidate') setChip('candidates'); },
    tree_of: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n <= 0) return;
      // A tree is a set of people, not a triage state: the ladder's chip goes to Everyone so
      // the whole tree is on screen, and the tree chip says what narrowed it.
      setTreeWanted(n);
      setChip('everyone');
    },
  });

  const rows = board?.rows ?? [];

  const cellIds = segment?.ids ?? null;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const inTree = treeOf ? rows.filter((r) => treeOf.ids.has(r.id)) : rows;
    // The cell filters are the server's answer, exactly as the tree is: this page narrows to
    // the ids it was given and never decides for itself who is a cadre supérieur.
    const inCells = cellIds ? inTree.filter((r) => cellIds.has(r.id)) : inTree;
    const byChip = inCells.filter((r) => {
      switch (chip) {
        case 'needs_move': return r.rank <= 2;
        case 'tier_a': return r.tier === 'A';
        case 'cooling': return r.cooling;
        case 'new': return r.is_new;
        case 'candidates': return false;
        case 'everyone': return true;
      }
    });
    const matched = !needle ? byChip : byChip.filter((r) => [r.name, r.organisation_name, r.role_title, r.ref]
      .some((f) => (f ?? '').toLowerCase().includes(needle)));

    // The board arrives ranked by the rule that fired. "By score" re-orders the SAME set by the
    // number the rules produced — a second reading of the same ladder, not a second ladder. A
    // person with no score sorts last rather than as a zero: nobody has scored them.
    if (sort !== 'score') return matched;
    return [...matched].sort((a, b) => {
      const sa = scores.get(a.id);
      const sb = scores.get(b.id);
      if (sa == null && sb == null) return a.rank - b.rank;
      if (sa == null) return 1;
      if (sb == null) return -1;
      return sb - sa || a.rank - b.rank;
    });
  }, [rows, chip, query, sort, scores, treeOf, cellIds]);

  // A different filter, sort or search is a different set: start again at the first page.
  useEffect(() => { setShown(PAGE_SIZE); }, [chip, query, sort, cellSel]);

  const visible = filtered.slice(0, shown);
  const lead = visible[0] ?? null;
  const rest = visible.slice(1);
  const remaining = filtered.length - visible.length;

  const openPerson = (id: number) => openRoute(ROUTES.person({ id }));

  const submitNew = async () => {
    const name = draft.name.trim();
    if (!name) { toast('err', t('A person needs a name.')); return; }
    try {
      const person = await createPerson({ name, tier: draft.tier });
      setNewOpen(false);
      setDraft({ name: '', tier: 'C', role: '' });
      toast('ok', t('{name} added as {ref}.', { name: person.name, ref: person.ref }));
      await refresh();
      openPerson(person.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  // The chip band's own count, one per chip. `new` is not a `board.chips` field — that object
  // mirrors the needs-a-move ladder — so its count is read off the rail's `new_7d` instead.
  const chipCount = (c: Chip): number | undefined => {
    if (!board) return undefined;
    if (c === 'new') return board.rail.new_7d;
    // `candidates` is the zone's producer, not the board's — the same reconciliation as the rail.
    if (c === 'candidates') return candidates ?? undefined;
    return board.chips[c];
  };

  // ── the rail: five cells, always, whatever the board says ──
  interface Cell {
    label: string;
    value: ReactNode;
    unit?: ReactNode;
    tone: RailTone;
    detail: ReactNode;
    onClick?: () => void;
    pressed?: boolean;
  }
  const railCells: Cell[] | null = board ? [
    {
      label: 'Held', value: <Fig>{board.rail.held}</Fig>, tone: 'ink' as RailTone,
      detail: t('{orgs} organisations · {a} tier A', { orgs: board.rail.organisations, a: board.rail.tier_a }),
      onClick: () => setChip('everyone'), pressed: chip === 'everyone',
    },
    {
      // Same producer as the Candidates zone below (see `candidates` above), so the cell and
      // the zone head always carry the same number.
      label: 'Candidates',
      value: candidates == null ? t('not collected') : <Fig>{candidates}</Fig>,
      tone: (candidates == null ? 'unknown' : candidates ? 'ink' : 'nil') as RailTone,
      detail: candidates == null
        ? t('the candidates could not be read')
        : candidates
          ? t('named by evidence · not on the record')
          : t('nothing proposed itself as a person'),
      onClick: () => setChip('candidates'), pressed: chip === 'candidates',
    },
    {
      label: 'Ball', value: <Fig>{board.rail.ball_us}</Fig>,
      unit: board.rail.ball_late
        ? t('us · {n} late', { n: board.rail.ball_late })
        : t('us'),
      tone: (board.rail.ball_late ? 'broken' : board.rail.ball_us ? 'attend' : 'nil') as RailTone,
      detail: t('{n} with them', { n: board.rail.ball_them }),
      onClick: () => setChip('needs_move'), pressed: chip === 'needs_move',
    },
    {
      label: 'Cooling', value: <Fig>{board.rail.cooling}</Fig>,
      tone: (board.rail.cooling ? 'attend' : 'nil') as RailTone,
      detail: board.rail.cooling
        ? t('past their tier cadence · {a} of them tier A', { a: board.rail.cooling_tier_a })
        : t('nobody is past their tier cadence'),
      onClick: () => setChip('cooling'), pressed: chip === 'cooling',
    },
    {
      label: 'New · 7d', value: <Fig>{board.rail.new_7d}</Fig>,
      tone: 'ink' as RailTone,
      detail: t('{i} from the import · {o} from you', { i: board.rail.new_import, o: board.rail.new_operator }),
    },
  ] : null;

  return (
    <PageShell>
      <Mast
        title={t('People')}
        sub={board ? (
          <>
            {t('ranked by the rule that fired')}<Sep />
            <Fig>{board.rail.held}</Fig> {t('held')}<Sep />
            {candidates != null && <><Fig>{candidates}</Fig> {t('candidates')}<Sep /></>}
            {t('name is the last tie-break')}
          </>
        ) : t('ranked by the rule that fired')}
        verbs={
          <>
            {notice && <span className="zone-note">{notice}</span>}
            <button type="button" className="btn btn-accent" onClick={() => setNewOpen(true)}>{t('New person')}</button>
          </>
        }
      >
        {/* §6.2's honest count. It is never a bare number: the three figures always travel with
            the cells they were counted on and with the size of the set they were counted over,
            so three zeros read as "nobody's function is on the record", never as "nobody
            matched". A read that failed shows no figures at all. */}
        {segment
          ? <CellCountLine counts={segment.counts} total={segment.total} lang={lang} />
          : segmentError
            ? <span className="fd" title={segmentError}>{t('the cell count could not be read: {error}', { error: segmentError })}</span>
            : null}
      </Mast>

      <Rail
        after={
          <>
          <div className="instr-band">
            <div className="filter-chips" style={{ margin: 0 }}>
              {/* The tree filter says what it is and how to leave: it narrows every chip beside
                  it, so it is drawn first and carries the clear. */}
              {treeOf && (
                <button
                  type="button"
                  className="chip active"
                  aria-pressed
                  title={t('Everyone affiliated with {name} or with a unit under it. Click to see the whole register again.', { name: treeOf.label })}
                  onClick={() => { setTreeWanted(null); setTreeOf(null); }}
                >
                  {t('In the tree of {name}', { name: treeOf.label })} <Fig>{treeOf.ids.size}</Fig> ×
                </button>
              )}
              {(Object.keys(CHIP_LABEL) as Chip[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip${chip === c ? ' active' : ''}`}
                  aria-pressed={chip === c}
                  onClick={() => setChip(c)}
                >
                  {t(CHIP_LABEL[c])}
                  {board && c !== 'everyone' && <> <Fig>{chipCount(c)}</Fig></>}
                </button>
              ))}
            </div>
            {/* The sort is a second, smaller band on the same row: it re-orders what the chips
                already chose, and never changes WHICH people are shown. */}
            <div className="filter-chips" style={{ margin: 0 }}>
              <span className="u-label" style={{ alignSelf: 'center', marginRight: 2 }}>{t('Sort')}</span>
              {(Object.keys(SORT_LABEL) as Sort[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`chip${sort === s ? ' active' : ''}`}
                  aria-pressed={sort === s}
                  onClick={() => setSort(s)}
                  title={s === 'score'
                    ? t('Order by the score the rules produced. Somebody nobody has scored sorts last.')
                    : t('Order by the rule that fired: late, then ours, then cooling.')}
                >
                  {t(SORT_LABEL[s])}
                </button>
              ))}
            </div>
            <div className="search-wrapper ml-auto" style={{ flex: '0 1 260px' }}>
              <input
                className="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Name, organisation, role, ref…')}
                aria-label={t('Search people')}
              />
            </div>
          </div>
          {/* THE CELL FILTERS (§6.2) — a second band, because these ask the server a question in
              the raster's own words rather than narrowing what is already on screen. Every label
              and every option is `personLabelFor`'s; the page spells none of them itself. */}
          <div className="instr-band">
            <span className="u-label">{t('Cells')}</span>
            <div className="filter-cells">
              {CELL_FILTERS.map((spec) => (
                <FilterSelect
                  key={spec.param}
                  label={cellFilterLabel(spec, lang)}
                  value={cellSel[spec.param] ?? ''}
                  options={cellFilterOptions(spec, lang, t)}
                  anyLabel={t('any')}
                  title={findPersonCell(spec.key)?.cell.sources_hint}
                  onChange={(v) => setCellSel((prev) => ({ ...prev, [spec.param]: v }))}
                />
              ))}
              {activeCells.length > 0 && (
                <button
                  type="button"
                  className="chip"
                  onClick={() => setCellSel({})}
                  title={t('Ask the register nothing about its cells again; the chips and the search stay as they are.')}
                >
                  {t('Clear cells')} <Fig>{activeCells.length}</Fig> ×
                </button>
              )}
            </div>
          </div>
          </>
        }
      >
        {railCells
          ? railCells.map((c) => (
              <RailCell
                key={c.label}
                label={t(c.label)}
                go={c.onClick ? '→' : undefined}
                value={c.value}
                unit={c.unit}
                tone={c.tone}
                detail={c.detail}
                onClick={c.onClick}
                pressed={c.pressed}
              />
            ))
          : ['Held', 'Candidates', 'Ball', 'Cooling', 'New · 7d'].map((label) => (
              <RailCell
                key={label}
                label={t(label)}
                value={t('not collected')}
                tone="unknown"
                detail={loading ? t('the board is still loading') : t('the board did not answer')}
              />
            ))}
      </Rail>

      {error && !board && (
        <Zone>
          <ZoneHead label={t('People')} />
          <EmptyState
            kind="error"
            title={t('The board did not answer')}
            detail={<>GET /api/persons/board — {error}</>}
            onRetry={() => { void refresh(); }}
          />
        </Zone>
      )}

      {board && chip !== 'candidates' && (
        <Zone id="needs-a-move">
          <ZoneHead
            label={t(CHIP_LABEL[chip])}
            count={<Fig>{filtered.length}</Fig>}
            note={t('the lead card is the person the data says to write to first')}
          />

          {lead ? (
            <>
              <Sheet
                edge
                pad
                className="row--btn"
                role="button"
                tabIndex={0}
                // `.row--btn` zeroes the border to make a row clickable; the lead card is a
                // sheet first, so its ink edge is put back explicitly (as the mockup does).
                style={{ display: 'block', marginBottom: 12, width: '100%', textAlign: 'left', borderLeft: '2px solid var(--ink-1)' }}
                onClick={() => openPerson(lead.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPerson(lead.id); } }}
              >
                <div className="move-head">
                  <h2>
                    {lead.name}
                    <span className={`statedot${lead.state === 'quiet' ? ' statedot--idle' : ''}`} />
                    <span className="statelabel">{t(STATE_LABEL[lead.state])}</span>
                  </h2>
                  <div className="figs move-cta">
                    <div className="f">
                      <span className="k">{t('Ball')}</span>
                      <span className={`v${lead.ball_late ? ' err' : lead.ball === 'us' ? ' warn' : lead.ball ? '' : ' nil'}`}>
                        {lead.ball ? t(lead.ball) : '—'}
                        {lead.ball_due && <span className="u">{fmtDate(lead.ball_due)}</span>}
                      </span>
                    </div>
                    <div className="f">
                      <span className="k">{t('Last contact')}</span>
                      <span className={`v${lead.days_since === null ? ' nil' : lead.cooling ? ' warn' : ''}`}>
                        {lead.days_since === null ? '—' : lead.days_since}
                        {lead.days_since !== null && <span className="u">d</span>}
                      </span>
                    </div>
                    <div className="f">
                      <span className="k">{t('Tier')}</span>
                      <span className="v">{lead.tier}<span className="u">{lead.cadence_days}d</span></span>
                    </div>
                    {(scores.get(lead.id) ?? 0) > 0 && (
                      <div className="f">
                        <span className="k">{t('Score')}</span>
                        <span className="v">{scores.get(lead.id)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="read" style={{ margin: '6px 0 0' }}>
                  {lead.role_title && <>{lead.role_title}{lead.role_proposed && <> <Att kind="proposed" /></>}, </>}
                  {lead.organisation_name ?? t('no organisation on record')} · <Ref>{lead.ref}</Ref>. {lead.note}.
                  {/* §6.2: the cells this segment was cut on, with the mark each value carries. */}
                  <CellChips cells={segment?.cells.get(lead.id)} lang={lang} />
                </p>
              </Sheet>

              <Sheet clip>
                {rest.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={`row row--btn${r.is_new ? ' row--new' : ''}`}
                    style={ROW_COLUMNS}
                    onClick={() => openPerson(r.id)}
                  >
                    <span>
                      <span className="w">{r.name}</span>
                      <span className="d">
                        {r.role_title && <> · {r.role_title}{r.role_proposed && <> <Att kind="proposed" /></>}</>}
                        {r.organisation_name && <> · {r.organisation_name}</>}
                        {' · '}<Ref>{r.ref}</Ref>
                      </span>
                      <br />
                      <span className="d">
                        {r.note}
                        <CellChips cells={segment?.cells.get(r.id)} lang={lang} />
                      </span>
                    </span>
                    <span className={`d${r.ball ? '' : ' n-none'}`}>
                      {r.ball
                        ? `${t('ball')} ${t(r.ball)}${r.ball_due ? ` · ${r.ball_late ? t('late since') : t('due')} ${fmtDate(r.ball_due)}` : ''}`
                        : '—'}
                    </span>
                    <span
                      className="voice-figure"
                      style={{ color: r.days_since === null ? 'var(--ink-4)' : r.cooling ? 'var(--attend)' : undefined }}
                      title={r.last_on ? t('last interaction {date}', { date: fmtDate(r.last_on) }) : t('no interaction on record')}
                    >
                      {r.days_since === null ? '—' : `${r.days_since}d`}
                    </span>
                    <span className="d">{r.tier} · {r.cadence_days}d</span>
                    {/* A score is arithmetic over rules: a figure, no claim mark. Nobody scored
                        yet is an EMPTY cell, never 0 — the two mean different things. */}
                    <span
                      className="voice-figure"
                      title={(scores.get(r.id) ?? 0) > 0 ? t('score') : t('not scored')}
                    >
                      {(scores.get(r.id) ?? 0) > 0 ? scores.get(r.id) : ''}
                    </span>
                    <Tag tone={STATE_TONE[r.state]}>{t(STATE_LABEL[r.state])}</Tag>
                  </button>
                ))}
                {rest.length === 0 && (
                  <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                    <span className="d">{t('One row matched, and it is the card above.')}</span>
                  </div>
                )}
                {remaining > 0 && (
                  <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                    <span className="d">
                      <button
                        type="button"
                        className="lnk"
                        onClick={() => setShown((n) => n + PAGE_SIZE)}
                      >
                        {t('Show {n} more', { n: Math.min(PAGE_SIZE, remaining) })}
                      </button>
                      <Sep />
                      {t('{shown} of {total} shown', { shown: visible.length, total: filtered.length })}
                    </span>
                  </div>
                )}
              </Sheet>
            </>
          ) : chip === 'needs_move' && !query.trim() && activeCells.length === 0 ? (
            <p className="zone-empty">
              {t('Nobody needs a move. A person appears here when the ball is with us, when it is late, or when they have cooled past their tier’s cadence.')}
            </p>
          ) : query.trim() || chip !== 'everyone' || activeCells.length > 0 ? (
            <EmptyState
              kind="no-match"
              title={t('Nobody matches')}
              /* A cell filter that excluded everyone is NAMED, because "nobody matches" while a
                 select quietly holds a token is the one way this band could lie. */
              detail={t('The filter is “{chip}”{q}{cells}. There are {n} people on the record.', {
                chip: t(CHIP_LABEL[chip]),
                q: query.trim() ? t(' and the search is “{q}”', { q: query.trim() }) : '',
                cells: activeCells.length
                  ? t(' and the cells are {cells}', {
                      cells: activeCells
                        .map((f) => `${cellFilterLabel(f, lang)} = ${cellFilterValueLabel(f, cellSel[f.param], lang, t)}`)
                        .join(' · '),
                    })
                  : '',
                n: board.rail.held,
              })}
              onClear={() => { setChip('everyone'); setQuery(''); setCellSel({}); }}
            />
          ) : (
            <EmptyState
              kind="empty"
              title={t('Nobody on the record yet')}
              detail={t('A person arrives from the seed import, from a held candidate proposal, or from New person. Until one of those happens this register is genuinely empty.')}
            />
          )}
        </Zone>
      )}

      {/* The Candidates zone is a COMPONENT the personas agent owns (§10, §14): it knows the
          persona that produced each candidate and the evidence behind it, which this board
          never sees. The board keeps the chip and the rail count; the zone keeps the rows. */}
      <CandidatesZone query={query} onPromote={() => { void refresh(); }} />

      <ConfirmModal
        open={newOpen}
        title={t('New person')}
        confirmLabel={t('Add to the record')}
        onCancel={() => setNewOpen(false)}
        onConfirm={() => { void submitNew(); }}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="np-name">{t('Name')}</label>
          <input
            id="np-name"
            className="form-input"
            value={draft.name}
            autoFocus
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder={t('Full name as they write it')}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="np-tier">{t('Tier')}</label>
          <select
            id="np-tier"
            className="form-select"
            value={draft.tier}
            onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value as Tier }))}
          >
            <option value="A">{t('A · 30 day cadence')}</option>
            <option value="B">{t('B · 90 day cadence')}</option>
            <option value="C">{t('C · 180 day cadence')}</option>
          </select>
        </div>
        <p className="zone-empty" style={{ marginTop: 12 }}>
          {t('The person is written with the correspondence language French and no affiliation. Attach an organisation and set the register from their brief.')}
        </p>
      </ConfirmModal>
    </PageShell>
  );
}
